export const APP_MODULES = {
    CLAIM: 'claim',
    COMPLIANCE: 'compliance',
    RISK: 'risk',
    BILLING: 'billing',
    /**
     * @deprecated Legacy umbrella module being retired in favour of the per-tool keys below
     * (`AUTO_DETECT`, `COMPLIANCE_AUDIT`, `DOCUMENT_ANALYZER`, `WEBSITE_CHECKER`, `PDS_COMPARE`).
     * Tolerated during the migration bridge; removed once no org row still carries `'tools'`.
     * See docs/plan/retire-tools-module-per-tool-enablement/plan.md.
     */
    TOOLS: 'tools',
    MSPR: 'mspr',
    ATTESTATIONS: 'attestations',
    FLEET: 'fleet',
    // Per-tool keys (formerly bundled under the `tools` umbrella module).
    AUTO_DETECT: 'auto-detect',
    COMPLIANCE_AUDIT: 'compliance-audit',
    DOCUMENT_ANALYZER: 'document-analyzer',
    WEBSITE_CHECKER: 'website-checker',
    PDS_COMPARE: 'pds-compare',
} as const;

export type AppModule = (typeof APP_MODULES)[keyof typeof APP_MODULES];

/**
 * The per-tool module keys that replaced the legacy `tools` umbrella. These render under the
 * "Enabled Tools" section in the System console and gate the individual tools under `/console/.../tools`.
 */
export const APP_TOOL_MODULES = [
    APP_MODULES.AUTO_DETECT,
    APP_MODULES.COMPLIANCE_AUDIT,
    APP_MODULES.DOCUMENT_ANALYZER,
    APP_MODULES.WEBSITE_CHECKER,
    APP_MODULES.PDS_COMPARE,
] as const satisfies readonly AppModule[];

export type AppToolModule = (typeof APP_TOOL_MODULES)[number];

/**
 * Modules that qualify an organisation for handling-party features. Handling parties own claims,
 * complaints, incidents, obligations, risks and controls, so holding any one of these is enough
 * (any-of, matching `staticData.modules` / `meta.appModules` semantics). Read by the client
 * handling-parties route gate and the Service API scope policy so the two cannot drift; an org
 * that holds none of these (tools-only, or no modules yet) has no handling-party surface.
 */
export const HANDLING_PARTY_MODULES = [
    APP_MODULES.CLAIM,
    APP_MODULES.COMPLIANCE,
    APP_MODULES.RISK,
] as const satisfies readonly AppModule[];

/**
 * Business (non-tool) modules — the product areas. Used to distinguish a "tools-only" org
 * (only tool modules enabled, no business module) for landing-page routing.
 */
export const APP_BUSINESS_MODULES = [
    APP_MODULES.CLAIM,
    APP_MODULES.COMPLIANCE,
    APP_MODULES.RISK,
    APP_MODULES.BILLING,
    APP_MODULES.MSPR,
    APP_MODULES.ATTESTATIONS,
    APP_MODULES.FLEET,
] as const satisfies readonly AppModule[];

/**
 * True when the org has at least one module enabled and none of them is a business module
 * (i.e. only tool modules and/or the legacy `tools` umbrella). Such orgs get the simplified
 * tools-only home page. Replaces the old `includes('tools') && length === 1` check, which breaks
 * once an org carries multiple per-tool module keys.
 */
export function isToolsOnlyOrg(enabledModules: readonly string[]): boolean {
    const businessModules = APP_BUSINESS_MODULES as readonly string[];
    return enabledModules.length > 0 && !enabledModules.some((module) => businessModules.includes(module));
}
